import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, FormControlLabel, FormHelperText, Grid, Paper, Switch, TextField, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../store';
import { useAuth } from '../hooks/useAuth';
import { updateProfile } from '../store/slices/authSlice';

interface ProfileFormData {
  password: string;
  password_confirm: string;
  first_name: string;
  last_name: string;
  is_developer: boolean;
}

const Profile: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  // Reduxからユーザー情報とローディング状態を取得
  const { user, isLoading, error } = useAuth();
  // 成功メッセージのステート
  const [ showSuccess, setShowSuccess ] = useState(false);
  // フォームに変更があるか追跡するステート
  const [ formChanged, setFormChanged ] = useState(false);
  // フォームデータ
  const [ formData, setFormData ] = useState<ProfileFormData>({
    password: '',
    password_confirm: '',
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    is_developer: user?.is_developer || false,
  });
  // 初期データ（比較用）
  const [ initialData, setInitialData ] = useState<Omit<ProfileFormData, 'password' | 'password_confirm'>>({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    is_developer: user?.is_developer || false,
  });
  // ページ読み込み時にローカルストレージから成功状態を確認
  useEffect(() => {
    const wasSuccessful = localStorage.getItem('profile_update_success') === 'true';
    const updateTime = localStorage.getItem('profile_update_time');
    if (wasSuccessful && updateTime) {
      // 最近の更新かどうかチェック（30秒以内）
      const timeDiff = Date.now() - parseInt(updateTime);
      if (timeDiff < 30000) {
        setShowSuccess(true);
        // フラグをクリア
        localStorage.removeItem('profile_update_success');
        localStorage.removeItem('profile_update_time');
      }
    }
  }, []);
  // ユーザー情報が変わったらフォームデータと初期データを更新
  useEffect(() => {
    if (user) {
      const userData = {
        first_name: user.first_name,
        last_name: user.last_name,
        is_developer: user.is_developer
      };
      setFormData(prevData => ({
        ...prevData,
        ...userData
      }));
      // 初期データも更新
      setInitialData(userData);
      // フォーム変更状態をリセット
      setFormChanged(false);
    }
  }, [ user ]);
  // テキストフィールド変更ハンドラー
  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    handleFormChange(name, value);
  };
  // スイッチ変更ハンドラー
  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    handleFormChange(name, checked);
  };
  // 共通のフォーム変更処理
  const handleFormChange = (name: string, value: any) => {
    const newFormData = {
      ...formData,
      [name]: value
    };
    setFormData(newFormData);
    // フォームに変更があるかチェック
    const hasNameChange = newFormData.first_name !== initialData.first_name ||
      newFormData.last_name !== initialData.last_name;
    const hasDeveloperChange = newFormData.is_developer !== initialData.is_developer;
    const hasPasswordChange = newFormData.password !== '' &&
      newFormData.password === newFormData.password_confirm;
    setFormChanged(hasNameChange || hasPasswordChange || hasDeveloperChange);
  };
  // 成功メッセージを閉じるハンドラー
  const handleCloseSuccess = () => {
    setShowSuccess(false);
  };
  // フォーム送信ハンドラー
  const handleSubmit = async(e: React.FormEvent) => {
    e.preventDefault();
    // 成功メッセージを非表示に
    setShowSuccess(false);
    // パスワード確認チェック
    if (formData.password && formData.password !== formData.password_confirm) {
      alert('パスワードが一致しません');
      return;
    }
    // 更新データの準備
    const updateData = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      is_developer: formData.is_developer,
    };
    // パスワードが入力されていれば追加
    if (formData.password) {
      // @ts-ignore
      updateData.password = formData.password;
      // @ts-ignore
      updateData.password_confirm = formData.password_confirm;
    }
    try {
      const resultAction = await dispatch(updateProfile(updateData));
      // 実際にAPIコールが成功したかチェック
      if (updateProfile.fulfilled.match(resultAction)) {
        // ブラウザストレージを使用して成功状態を保存
        localStorage.setItem('profile_update_success', 'true');
        localStorage.setItem('profile_update_time', Date.now().toString());
        // 初期データを更新
        setInitialData({
          first_name: formData.first_name,
          last_name: formData.last_name,
          is_developer: formData.is_developer
        });
        // パスワードフィールドをクリア
        setFormData({
          ...formData,
          password: '',
          password_confirm: ''
        });
        // フォーム変更状態をリセット
        setFormChanged(false);
        // ページをリロード
        window.location.reload();
      }
    } catch (err) {
      // エラー処理はReduxのリジェクト時に自動的に行われる
    }
  };
  // 未認証の場合
  if (!user) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">認証されていません。ログインしてください。</Alert>
      </Box>
    );
  }
  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          プロフィール設定
        </Typography>

        {/* エラーメッセージ */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* 成功メッセージ - showSuccessがtrueの時だけ表示、閉じるボタン付き */}
        {showSuccess && (
          <Alert
            severity="success"
            variant="standard"
            onClose={handleCloseSuccess}
            sx={{
              mb: 2,
              p: 1
            }}
          >
            更新しました
          </Alert>
        )}

        {/* フォーム */}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                disabled
                label="メールアドレス"
                value={user.email}
                variant="outlined"
                helperText="メールアドレスはユーザー自身では変更できません。変更が必要な場合は管理者にお問い合わせください。"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="姓"
                name="last_name"
                value={formData.last_name}
                onChange={handleTextChange}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="名"
                name="first_name"
                value={formData.first_name}
                onChange={handleTextChange}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="新しいパスワード"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleTextChange}
                variant="outlined"
                helperText={
                  <span>
                    変更する場合のみ入力してください。<br/>
                    ・8文字以上<br/>
                    ・英字を含む<br/>
                    ・数字を含む<br/>
                    ・記号（!@#$%^&*など）を含む<br/>
                    ・よく使われるパスワードは避ける<br/>
                    ・ユーザー名やメールアドレスと似たものは避ける
                  </span>
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="パスワード（確認）"
                name="password_confirm"
                type="password"
                value={formData.password_confirm}
                onChange={handleTextChange}
                variant="outlined"
                helperText="新しいパスワードを再入力してください（上記と同じパスワード）"
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ mt: 2, mb: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      name="is_developer"
                      checked={formData.is_developer}
                      onChange={handleSwitchChange}
                      color="primary"
                    />
                  }
                  label="開発者モード"
                />
                <FormHelperText>
                  開発者モードを有効にすると、システム内部情報などの追加情報が表示されます
                </FormHelperText>
              </Box>
            </Grid>

            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isLoading || !formChanged}
              >
                {isLoading ? <CircularProgress size={24}/> : '更新する'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};
export default Profile;
